import { ActionContext, ActionTree, GetterTree } from "vuex";
import { ReservationSearchData } from "@/model/ReservationSearchData";
import { ShakenStorage } from "@/service/storage";

const getSessionStorageKey = () => ShakenStorage.getSessionKey("reservation-search-data");

interface ReservationSearchDataState {
  searchData: ReservationSearchData;
  currentTab: number;
  currentPage: number;
  currentDay: number;
  rangeDay: number;
  startDay: string;
  searchMode: boolean;
}

const initialSearchData: ReservationSearchData = {
  plateArea: "",
  plateModel: "",
  plateKana: "",
  plateNo: "",
  workShubetsu: "",
  kkUketsukeNm: "",
  kkUketsukeNmKn: "",
  shadaiNo: "",
  staffKensain: [],
  staffSeibishi: [],
  staffHojoseibishi: [],
  shuruiSeibiMenu: [],
  cntSeibiImageType: 0,
  cntCarImageType: 0,
  yoyakuNo: "",
  kkTel: "",
  skNo: "",
  kkNo: "",
  nyuukoPlansStart: "",
  nyuukoPlansEnd: ""
};

const initialState: ReservationSearchDataState = {
  searchData: { ...initialSearchData },
  currentTab: 0,
  currentPage: 1,
  currentDay: 6,
  rangeDay: 7,
  startDay: "",
  searchMode: false
};

const saveSession = (state: ReservationSearchDataState) => {
  window.sessionStorage.setItem(getSessionStorageKey(), JSON.stringify(state));
};

const normalizeArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map(v => String(v));
  }
  if (value === null || value === undefined || value === "") {
    return [];
  }
  return [String(value)];
};

export const clearReservationSearchDataSession = (): void => {
  window.sessionStorage.removeItem(getSessionStorageKey());
};

const actions: ActionTree<ReservationSearchDataState, any> = {
  setState(
    { commit }: ActionContext<ReservationSearchDataState, any>,
    payload: ReservationSearchData
  ): void {
    commit("setState", payload);
  },
  setCurrentTab(
    { commit }: ActionContext<ReservationSearchDataState, any>,
    payload: number
  ): void {
    commit("setCurrentTab", payload);
  },
  setCurrentPage(
    { commit }: ActionContext<ReservationSearchDataState, any>,
    payload: number
  ): void {
    commit("setCurrentPage", payload);
  },
  setCurrentDay(
    { commit }: ActionContext<ReservationSearchDataState, any>,
    payload: number
  ): void {
    commit("setCurrentDay", payload);
  },
  setRangeDay(
    { commit }: ActionContext<ReservationSearchDataState, any>,
    payload: number
  ): void {
    commit("setRangeDay", payload);
  },
  setNyuukoPlansRange(
    { commit }: ActionContext<ReservationSearchDataState, any>,
    payload: string[] | null
  ): void {
    commit("setNyuukoPlansRange", payload);
  },
  setStartDay(
    { commit }: ActionContext<ReservationSearchDataState, any>,
    payload: string
  ): void {
    commit("setStartDay", payload);
  },
  setSearchMode(
    { commit }: ActionContext<ReservationSearchDataState, any>,
    payload: boolean
  ): void {
    commit("setSearchMode", payload);
  },
  toggleSearchMode(
    { commit }: ActionContext<ReservationSearchDataState, any>
  ): void {
    commit("toggleSearchMode");
  },
  clearSearchForm(
    { commit }: ActionContext<ReservationSearchDataState, any>
  ): void {
    commit("clearSearchForm");
  },
  clearState({ commit }: ActionContext<ReservationSearchDataState, any>): void {
    commit("clearState");
  },
  setStateSession(
    { commit }: ActionContext<ReservationSearchDataState, any>
  ): void {
    commit("setStateSession");
  }
};

const mutations = {
  setState(state: ReservationSearchDataState, payload: ReservationSearchData) {
    Object.assign(state.searchData, payload);
    saveSession(state);
  },
  setCurrentTab(state: ReservationSearchDataState, payload: number) {
    state.currentTab = payload;
    saveSession(state);
  },
  setCurrentPage(state: ReservationSearchDataState, payload: number) {
    state.currentPage = payload;
    saveSession(state);
  },
  setCurrentDay(state: ReservationSearchDataState, payload: number) {
    state.currentDay = payload;
    saveSession(state);
  },
  setRangeDay(state: ReservationSearchDataState, payload: number) {
    state.rangeDay = payload;
    saveSession(state);
  },
  setNyuukoPlansRange(
    state: ReservationSearchDataState,
    payload: string[] | null
  ) {
    if (payload && payload.length === 2) {
      state.searchData.nyuukoPlansStart = payload[0];
      state.searchData.nyuukoPlansEnd = payload[1];
    } else {
      state.searchData.nyuukoPlansStart = "";
      state.searchData.nyuukoPlansEnd = "";
    }
    saveSession(state);
  },
  setStartDay(state: ReservationSearchDataState, payload: string) {
    state.startDay = payload;
    saveSession(state);
  },
  setSearchMode(state: ReservationSearchDataState, payload: boolean) {
    state.searchMode = payload;
    saveSession(state);
  },
  toggleSearchMode(state: ReservationSearchDataState) {
    state.searchMode = !state.searchMode;
    saveSession(state);
  },
  clearSearchForm(state: ReservationSearchDataState) {
    state.searchData = { ...initialSearchData };
    saveSession(state);
  },
  clearState(state: ReservationSearchDataState) {
    Object.assign(state, {
      searchData: { ...initialSearchData },
      currentTab: initialState.currentTab,
      currentPage: initialState.currentPage,
      currentDay: initialState.currentDay,
      rangeDay: initialState.rangeDay,
      startDay: initialState.startDay,
      searchMode: initialState.searchMode
    });
    // saveSession(state);
  },
  setStateSession(state: ReservationSearchDataState) {
    const obj = window.sessionStorage.getItem(getSessionStorageKey());
    if (!obj) {
      return;
    }
    const savedData = JSON.parse(obj);
    const nextState: ReservationSearchDataState = {
      searchData: {
        ...initialSearchData,
        ...(savedData.searchData || savedData)
      },
      currentTab:
        savedData.currentTab !== undefined
          ? savedData.currentTab
          : initialState.currentTab,
      currentPage:
        savedData.currentPage !== undefined
          ? savedData.currentPage
          : initialState.currentPage,
      currentDay:
        savedData.currentDay !== undefined
          ? savedData.currentDay
          : initialState.currentDay,
      rangeDay:
        savedData.rangeDay !== undefined
          ? savedData.rangeDay
          : initialState.rangeDay,
      startDay:
        savedData.startDay !== undefined
          ? savedData.startDay
          : initialState.startDay,
      searchMode:
        savedData.searchMode !== undefined
          ? savedData.searchMode
          : initialState.searchMode
    };
    nextState.searchData.staffKensain = normalizeArray(nextState.searchData.staffKensain);
    nextState.searchData.staffSeibishi = normalizeArray(nextState.searchData.staffSeibishi);
    nextState.searchData.staffHojoseibishi = normalizeArray(nextState.searchData.staffHojoseibishi);
    nextState.searchData.shuruiSeibiMenu = normalizeArray(nextState.searchData.shuruiSeibiMenu);
    Object.assign(state, nextState);
  }
};

const getters: GetterTree<ReservationSearchDataState, any> = {
  currentState(state: ReservationSearchDataState): ReservationSearchData {
    return state.searchData;
  },
  currentTab(state: ReservationSearchDataState): number {
    return state.currentTab;
  },
  currentPage(state: ReservationSearchDataState): number {
    return state.currentPage;
  },
  currentDay(state: ReservationSearchDataState): number {
    return state.currentDay;
  },
  rangeDay(state: ReservationSearchDataState): number {
    return state.rangeDay;
  },
  searchPlansRange(state: ReservationSearchDataState): string[] {
    if (
      state.searchData.nyuukoPlansStart &&
      state.searchData.nyuukoPlansEnd
    ) {
      return [
        state.searchData.nyuukoPlansStart,
        state.searchData.nyuukoPlansEnd
      ];
    }
    return [];
  },
  startDay(state: ReservationSearchDataState): string {
    return state.startDay;
  },
  searchMode(state: ReservationSearchDataState): boolean {
    return state.searchMode;
  }
};

const namespaced: boolean = true;
export default {
  namespaced,
  state: {
    searchData: { ...initialSearchData },
    currentTab: initialState.currentTab,
    currentPage: initialState.currentPage,
    currentDay: initialState.currentDay,
    rangeDay: initialState.rangeDay,
    startDay: initialState.startDay,
    searchMode: initialState.searchMode
  },
  getters,
  actions,
  mutations
};
